import React, { useState, useEffect, useCallback } from 'react'
import { toEnglishNumber } from '$func/text'
import { OtpInput } from '$common/verification-code-input/otp-input'

interface Props {
  className?: string
  numFields: number
  hasError?: boolean
  onChange: (otp: string) => void
  timeout: number
  resendMaxed: boolean
  autoComplete?: boolean
}

let abortController = new AbortController()

export const VerificationCodeInput = ({
  className,
  numFields,
  hasError,
  onChange,
  timeout,
  resendMaxed,
  autoComplete = false,
}: Props) => {
  const [otp, setOtp] = useState('')

  const handleChange = useCallback(
    (otpCode: string) => {
      setOtp(toEnglishNumber(otpCode))
      onChange(toEnglishNumber(otpCode))
    },
    [onChange],
  )

  useEffect(() => {
    if (!autoComplete) return
    if ('OTPCredential' in window) {
      if (resendMaxed) {
        abortController.abort('Maximum number of resending otp code exceeded.')
        return
      }
      const input = document.querySelector(
        'input[autocomplete="one-time-code"]',
      )

      if (!input || !navigator.credentials) return
      abortController = new AbortController()
      setTimeout(() => {
        abortController.abort('Timeout exceeded before getting otp from sms.')
      }, timeout)

      navigator.credentials
        .get({
          otp: { transport: ['sms'] },
          signal: abortController.signal,
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((credential: any) => {
          // Attention: Cast to any because return type is Credential and Credential does not contain Code
          // https://stackoverflow.com/questions/67182406/web-otp-api-property-code-does-not-exist-on-type-credentialtype
          if (credential?.code) {
            handleChange(credential.code)
          }
        })
        .catch(err => {
          console.error(err)
        })
    }
  }, [timeout, resendMaxed, handleChange, autoComplete])

  return (
    <OtpInput
      numFields={numFields}
      onChange={handleChange}
      value={otp}
      hasError={hasError}
      autoComplete={autoComplete ? 'one-time-code' : undefined}
      autoFocus
      className={className}
    />
  )
}
